import { baseApi } from './baseApi';

// Shared query-string builder for ranged report endpoints. Every report that shows money
// derived from invoices also forwards `excludeHomeopathy`, so the global toggle on the
// Reports page filters consistently across ALL tabs (it used to reach only the dashboard).
const rangeQuery = (path) => ({ from, to, excludeHomeopathy } = {}) => {
  const p = new URLSearchParams();
  if (from) p.append('from', from);
  if (to) p.append('to', to);
  if (excludeHomeopathy) p.append('excludeHomeopathy', 'true');
  const s = p.toString();
  return `${path}${s ? `?${s}` : ''}`;
};

export const reportsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    dashboard: build.query({
      query: rangeQuery('/reports/dashboard'),
      transformResponse: (resp) => resp.data,
      providesTags: ['Dashboard'],
    }),
    leadFunnel: build.query({
      query: rangeQuery('/reports/lead-funnel'),
      transformResponse: (resp) => resp.data,
    }),
    leadSources: build.query({
      query: rangeQuery('/reports/lead-sources'),
      transformResponse: (resp) => resp.data,
    }),
    // Leads grouped by current status (counts + %), same source as the leads list.
    leadStatusBreakdown: build.query({
      query: rangeQuery('/reports/lead-status'),
      transformResponse: (resp) => resp.data,
      providesTags: [{ type: 'Lead', id: 'LIST' }],
    }),
    // Leads grouped by clinic department (Physiotherapy / Home Visit / Functional / Total Transformation).
    leadDepartmentBreakdown: build.query({
      query: rangeQuery('/reports/lead-departments'),
      transformResponse: (resp) => resp.data,
      providesTags: [{ type: 'Lead', id: 'LIST' }],
    }),
    staffPerformance: build.query({
      query: rangeQuery('/reports/staff'),
      transformResponse: (resp) => resp.data,
    }),
    staffDetail: build.query({
      query: ({ userId, ...range }) => rangeQuery(`/reports/staff/${userId}`)(range),
      transformResponse: (resp) => resp.data,
    }),
    doctorRevenueBreakdown: build.query({
      query: ({ userId, ...range }) => rangeQuery(`/reports/staff/${userId}/revenue-breakdown`)(range),
      transformResponse: (resp) => resp.data,
    }),
    // Cash collected in the range, by Payment.PaidAt — Receipts tab.
    receiptsReport: build.query({
      query: rangeQuery('/reports/receipts'),
      transformResponse: (resp) => resp.data,
      providesTags: ['Invoice'],
    }),
    // Google reviews, renewal pitching and renewals over the range — Patients tab.
    patientEngagement: build.query({
      query: rangeQuery('/reports/patient-engagement'),
      transformResponse: (resp) => resp.data,
      providesTags: [{ type: 'Patient', id: 'LIST' }],
    }),
    patientsReport: build.query({
      query: rangeQuery('/reports/patients'),
      transformResponse: (resp) => resp.data,
    }),
    procedureRevenue: build.query({
      query: rangeQuery('/reports/procedure-revenue'),
      transformResponse: (resp) => resp.data,
    }),
    patientSessions: build.query({
      query: (filter = {}) => {
        const p = new URLSearchParams();
        Object.entries(filter).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') p.append(k, v);
        });
        const s = p.toString();
        return `/reports/patient-sessions${s ? `?${s}` : ''}`;
      },
      transformResponse: (resp) => resp.data,
      providesTags: [{ type: 'Patient', id: 'LIST' }, { type: 'Session', id: 'LIST' }],
    }),
    operationalReport: build.query({
      query: rangeQuery('/reports/operational'),
      transformResponse: (resp) => resp.data,
    }),
    newPatientAnalysis: build.query({
      query: rangeQuery('/reports/new-patients'),
      transformResponse: (resp) => resp.data,
      // Tagged so the inline Source edit (a billing PATCH → invalidates Invoice LIST) refetches this.
      providesTags: [{ type: 'Invoice', id: 'LIST' }],
    }),
    renewalAnalysis: build.query({
      query: rangeQuery('/reports/renewals'),
      transformResponse: (resp) => resp.data,
    }),
    // Per-patient renewal ledger (2nd invoice = renewal #1) with period totals + average gaps.
    renewalLedger: build.query({
      query: rangeQuery('/reports/renewal-ledger'),
      transformResponse: (resp) => resp.data,
      providesTags: [{ type: 'Invoice', id: 'LIST' }],
    }),
    generalPerformance: build.query({
      query: rangeQuery('/reports/general-performance'),
      transformResponse: (resp) => resp.data,
    }),
    buildReport: build.mutation({
      query: (body) => ({ url: '/reports/build', method: 'POST', body }),
      transformResponse: (resp) => resp.data,
    }),
    calendarExport: build.query({
      query: ({ from, to }) => `/reports/calendar-export?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      transformResponse: (resp) => resp.data,
    }),
    clinicAnalytics: build.query({
      query: rangeQuery('/reports/clinic-analytics'),
      transformResponse: (resp) => resp.data,
    }),
  }),
});

export const {
  useDashboardQuery, useLeadFunnelQuery, useLeadSourcesQuery, useLeadStatusBreakdownQuery,
  useLeadDepartmentBreakdownQuery,
  useStaffPerformanceQuery, useStaffDetailQuery, useDoctorRevenueBreakdownQuery,
  usePatientsReportQuery, useProcedureRevenueQuery,
  usePatientSessionsQuery, useLazyPatientSessionsQuery, usePatientEngagementQuery, useReceiptsReportQuery,
  useOperationalReportQuery,
  useNewPatientAnalysisQuery, useRenewalAnalysisQuery, useRenewalLedgerQuery,
  useGeneralPerformanceQuery, useBuildReportMutation,
  useLazyCalendarExportQuery, useClinicAnalyticsQuery,
} = reportsApi;
